import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { hotelIdOf } from "@/lib/hotelRef";
import { PageHeader, Card, Badge, EmptyState } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Calendar, Hotel } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import { useDateFormat } from "@/lib/dateFormat";
import { RateInputWithTax, nightsBetween } from "@/components/pms/RateInput";
import { ReservationSourceInput } from "@/components/pms/ReservationSourceInput";
import { MergeBillsButton } from "@/components/documents/MergeBillsDialog";

const STATUS_TONE = { reserved: "warning", checked_in: "success", checked_out: "neutral", cancelled: "error", no_show: "error" };

const fmtStayDate = (fmt, v) => {
  if (!v) return "—";
  // Timestamps (contain a time component) → date + time in hotel tz; plain dates → date only.
  return String(v).includes("T") ? fmt.formatDateTime(v) : fmt.formatDate(v);
};

export default function Stays() {
  const dateFmt = useDateFormat();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(params.get("new") === "1");
  const [guests, setGuests] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [form, setForm] = useState({
    guest_id: params.get("guest_id") || "", hotel_id: "", type: "reservation",
    expected_arrival: "", expected_departure: "", base_rate: 0, entered_rate: "", tax_inclusive_input: false, rate_plan_id: "", num_adults: 1, initial_room_id: "",
    reservation_source: "", reservation_source_ref: "",
  });

  const load = async () => {
    const p = new URLSearchParams({ page_size: "100" });
    if (status) p.set("status", status);
    if (q) p.set("q", q);
    const { data } = await api.get(`/pms/stays/?${p}`);
    setItems(data.items);
  };
  useEffect(() => { load(); }, [status, q]);

  useEffect(() => {
    (async () => {
      // These feed the "New Stay" form dropdowns only. A Front Desk officer may
      // lack master-data read permissions (403) — that must not blank the list.
      const safe = (p) => p.then(r => r.data?.items || []).catch(() => []);
      const [guestsList, hotelsList, roomsList] = await Promise.all([
        safe(api.get("/pms/guests/?page_size=200")),
        safe(api.get("/master/hotel-profiles/?page_size=100")),
        safe(api.get("/master/rooms/?page_size=300")),
      ]);
      setGuests(guestsList); setHotels(hotelsList); setRooms(roomsList);
      if (hotelsList[0] && !form.hotel_id) setForm(f => ({ ...f, hotel_id: hotelIdOf(hotelsList[0]) }));
    })();
  }, []);

  const [taxConfigured, setTaxConfigured] = useState(true);
  const submit = async () => {
    if (!taxConfigured) { toast.error("Tax rates are not configured for this hotel — set them in Billing & Tax settings first"); return; }
    if (form.entered_rate !== "" && !(Number(form.entered_rate) > 0)) { toast.error("Nightly rate must be greater than zero"); return; }
    try {
      const payload = { ...form };
      if (!payload.initial_room_id) delete payload.initial_room_id;
      if (!payload.rate_plan_id) delete payload.rate_plan_id;
      if (payload.entered_rate === "") { delete payload.entered_rate; } else { payload.base_rate = Number(payload.entered_rate); }
      await api.post("/pms/stays/", payload);
      toast.success("Stay created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = {
    reserved: items.filter(s => s.status === "reserved").length,
    in_house: items.filter(s => s.status === "checked_in").length,
    departing: items.filter(s => s.status === "checked_in" && s.checkout_requested_at).length,
  };

  return (
    <div data-testid="stays-page">
      <PageHeader title="Stays" subtitle="Stay Management"
        action={<Button onClick={() => setOpen(true)} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="create-stay-button"><Plus className="w-3.5 h-3.5 mr-1.5"/> New stay</Button>}
      />

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Reserved</div><div className="font-mono text-2xl text-amber-400">{counts.reserved}</div></Card>
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">In house</div><div className="font-mono text-2xl text-emerald-400">{counts.in_house}</div></Card>
        <Card className="p-4"><div className="text-[10px] mono uppercase tracking-wider text-hocp-textMuted mb-1">Awaiting checkout</div><div className="font-mono text-2xl text-blue-400">{counts.departing}</div></Card>
      </div>

      <Card className="p-3 mb-4">
        <div className="flex flex-wrap gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Stay code or guest name…" aria-label="Search stays" className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] flex-1 min-w-[200px]" data-testid="stays-search"/>
          <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger className="bg-hocp-bgCard border-hocp-border h-9 text-[13px] w-[180px]" data-testid="stays-status-filter" aria-label="Filter by status"><SelectValue placeholder="Status"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {Object.keys(STATUS_TONE).map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {items.length === 0 ? <EmptyState icon={Calendar} title="No stays" subtitle="Create a reservation or walk-in to get started."/> :
        <div className="border border-hocp-border rounded-md bg-hocp-bgCard overflow-hidden">
          <table className="w-full">
            <thead><tr className="border-b border-hocp-border bg-hocp-bgSubtle">{["Code", "Guest", "Type", "Status", "Arrival", "Departure", "Room", ""].map((h, i) => <th key={i} className="px-4 py-2.5 text-[10px] font-medium uppercase tracking-[0.1em] text-hocp-textMuted mono text-left">{h}</th>)}</tr></thead>
            <tbody>
              {items.map(s => (
                <tr key={s.id} className="border-b border-hocp-border last:border-0 hover:bg-hocp-bgSubtle/70" data-testid={`stay-row-${s.code}`}>
                  <td className="px-4 py-2.5"><Link to={`/pms/stays/${s.id}`} className="mono text-hocp-textPrimary hover:text-hocp-deepTeal hover:underline text-[12px]" data-testid={`stay-link-${s.code}`}>{s.code}</Link></td>
                  <td className="px-4 py-2.5 text-[12px] text-white"><span className="inline-flex items-center gap-2">{s.guest_name}{s.group?.size > 1 && <Badge tone="info" data-testid={`stay-group-chip-${s.code}`}>Room {s.group.index} of {s.group.size}</Badge>}</span></td>
                  <td className="px-4 py-2.5"><Badge tone="info">{s.type}</Badge></td>
                  <td className="px-4 py-2.5"><Badge tone={STATUS_TONE[s.status] || "neutral"}>{s.status.toUpperCase().replace("_", " ")}</Badge></td>
                  <td className="px-4 py-2.5 text-[12px] mono text-hocp-textMuted">{fmtStayDate(dateFmt, s.actual_arrival || s.expected_arrival)}</td>
                  <td className="px-4 py-2.5 text-[12px] mono text-hocp-textMuted">{fmtStayDate(dateFmt, s.actual_departure || s.expected_departure)}</td>
                  <td className="px-4 py-2.5 text-[12px] mono text-hocp-textMuted">{s.room_number || rooms.find(r => r.id === s.current_room_id)?.room_number || "—"}</td>
                  <td className="px-4 py-2.5 text-right">{s.status === "checked_in" && <MergeBillsButton stay={s} testId={`merge-bills-${s.code}`} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      }

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-hocp-bgSubtle border-hocp-border max-w-xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden" data-testid="new-stay-dialog">
          <DialogHeader className="shrink-0 px-6 py-4 border-b border-hocp-border"><DialogTitle className="font-heading">New Stay</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="new-stay-body">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="stay-type"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="reservation">Reservation</SelectItem><SelectItem value="walkin">Walk-in</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Hotel</Label>
                <Select value={form.hotel_id} onValueChange={(v) => setForm({ ...form, hotel_id: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="stay-hotel"><SelectValue/></SelectTrigger>
                  <SelectContent>{hotels.map(h => <SelectItem key={h.id} value={hotelIdOf(h)}>{h.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Guest *</Label>
              <Select value={form.guest_id} onValueChange={(v) => setForm({ ...form, guest_id: v })}>
                <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="stay-guest"><SelectValue placeholder="Choose guest"/></SelectTrigger>
                <SelectContent className="max-h-[280px]">{guests.map(g => <SelectItem key={g.id} value={g.id}>{g.code} · {[g.first_name, g.last_name].join(" ")} · {g.primary_phone}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Expected arrival *</Label><div className="mt-1.5"><DatePicker value={form.expected_arrival} onChange={(v) => setForm({ ...form, expected_arrival: v })} data-testid="stay-arrival"/></div></div>
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Expected departure *</Label><div className="mt-1.5"><DatePicker value={form.expected_departure} onChange={(v) => setForm({ ...form, expected_departure: v })} minDate={form.expected_arrival} data-testid="stay-departure"/></div></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Adults</Label><Input type="number" value={form.num_adults} onChange={(e) => setForm({ ...form, num_adults: +e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono"/></div>
            <ReservationSourceInput testidPrefix="stay-source" value={{ reservation_source: form.reservation_source, reservation_source_ref: form.reservation_source_ref }} onChange={(v) => setForm({ ...form, ...v })} />
            <RateInputWithTax testidPrefix="stay-rate" nights={nightsBetween(form.expected_arrival, form.expected_departure)} roomTypeId={form.room_type_id}
              value={{ entered_rate: form.entered_rate, tax_inclusive_input: form.tax_inclusive_input, rate_plan_id: form.rate_plan_id }}
              onChange={(v) => setForm({ ...form, ...v })} onConfigured={setTaxConfigured} />
            {form.type === "walkin" && (
              <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Assign room now</Label>
                <Select value={form.initial_room_id} onValueChange={(v) => setForm({ ...form, initial_room_id: v })}>
                  <SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="stay-initial-room"><SelectValue placeholder="Select available room"/></SelectTrigger>
                  <SelectContent className="max-h-[260px]">{rooms.filter(r => r.operational_status === "available").map(r => <SelectItem key={r.id} value={r.id}>Room {r.room_number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="shrink-0 px-6 py-4 border-t border-hocp-border"><Button onClick={submit} disabled={!taxConfigured} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-stay-button">Create stay</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
